import React from 'react';
import {
  Heading,
  Flex,
  Circle,
  Image,
  Text,
  List,
  ListItem
} from '@chakra-ui/react';
import * as Icons from 'react-icons/fa6';
import DOMPurify from 'dompurify';
import appConfig from '../Config/appConfig';

/**
 * HeaderComponent dynamically renders headers with ADA-compliant attributes,
 * including the option for numbered lists.
 * @param {string|string[]} content - The text content of the header. Can be a string
 * for a single heading or a string with newline
 * characters ('\n') for a list.
 * @param {string} variant - The typography variant (e.g., "h1", "h2").
 * @param {object} icon - Icon object containing properties like name, url, etc.
 * @param {string} ariaLabel - Accessibility label for the header.
 * @param {string} role - Accessibility role for the header.
 * @param {number} listNumber - The starting number for a numbered list. If present,
 * the component renders an ordered list.
 */

const HeaderComponent = ({
  content,
  variant,
  icon = null,
  ariaLabel,
  role = 'heading',
  color = null,
  textAlign,
  marginBottom,
  marginTop,
  listNumber = null, // Prop for list starting number
}) => {
  const IconComponent = icon?.name ? Icons[icon.name] : null;
  const sanitizedContent = DOMPurify.sanitize(content);

  // Split content into list items if a listNumber is provided
  const listItems = listNumber !== null && typeof content === 'string'
    ? content.split('\n').filter(item => item.trim() !== '')
    : [];

  const textStyles = {
    fontFamily: `var(--${variant}-font-family, inherit)`,
    fontWeight: `var(--${variant}-font-weight, bold)`,
    fontSize: `var(--${variant}-font-size, inherit)`,
    lineHeight: `var(--${variant}-line-height, normal)`,
    letterSpacing: `var(--${variant}-letter-spacing, normal)`,
    color: color || `var(--${variant}-color, var(--sutter-sync-purple))`,
    textAlign: textAlign || `var(--${variant}-text-align, left)`,
  };

  return (
    <Flex
      alignItems={`var(--${variant}-align-items, center)`}
      justifyContent={textAlign || `var(--${variant}-justify-content, flex-start)`}
      gap={2}
      mb={marginBottom || 2}
      mt={marginTop || 0}
      role={role}
      aria-label={ariaLabel}
    >
      {/* Render Icon, Icon Text, or Image */}
      {icon && (icon.name || icon.url || icon.alphabet) && (
        <Circle
          size={`var(--icon-${icon.variant}-size)`}
          bgGradient={icon.variant ? "linear(180deg, #A12EFF 0%, #5F1B93 100%)" : "none"}
          role={icon.role || 'presentation'}
          border={`var(--${icon.variant}-border) var(--${icon.variant}-border-color) || transparent`}
        >
          {icon.url ? (
            <Image
              src={`${appConfig.ImageBlobBaseUrl}/${icon.url}`}
              alt=""
              aria-hidden="true"
              boxSize={`calc(${icon.size || `var(--${icon.variant}-size, 36px)`})`}
            />
          ) : icon.alphabet ? (
            <Text
              style={{
                fontFamily: `var(--${icon.textVariant}-font-family, Sora)`,
                fontWeight: `var(--${icon.textVariant}-font-weight, normal)`,
                fontSize: icon.size || `var(--${icon.textVariant}-font-size, 36px)`,
                lineHeight: `var(--${icon.textVariant}-line-height, 1.5)`,
                marginBottom: `var(--${icon.textVariant}-margin-bottom, 0)`,
                marginTop: `var(--${icon.textVariant}-margin-top, 4)`,
                color: icon.textColor || `var(--${icon.textVariant}-color, var(--sutter-sync-purple))`
              }}
              aria-hidden="true"
            >
              {icon.alphabet}
            </Text>
          ) : IconComponent ? (
            <IconComponent
              aria-hidden="true"
              style={{
                width: `${icon.size || `var(--${icon.variant}-size, 36px)`}`,
                height: `${icon.size || `var(--${icon.variant}-size, 36px)`}`,
                color: `var(--${icon.variant}-color, var(--sutter-sync-purple))`,
              }}
            />
          ) : null}
        </Circle>
      )}

      {/* Conditional Rendering: Render List or Heading */}
      {listNumber !== null && listItems.length > 0 ? (
        <List
          as="ol"
          styleType="decimal"
          start={listNumber}
          paddingLeft={icon?.padding ?? 0}
          // Apply typography styles directly to the List component
          // The list numbers will inherit these styles
          {...textStyles}
          style={{ listStylePosition: 'outside', marginLeft: '1.5em' }}
        >
          {listItems.map((item, index) => (
            <ListItem key={index}>
              <Text
                as="span"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Heading
          {...textStyles}
          paddingLeft={icon?.padding ?? 0}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      )}
    </Flex>
  );
};

export default HeaderComponent;